import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {filter, merge, NEVER, Observable, switchMap} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';
import {destroy$} from "../../destroyable";
import {WalletAvatarComponent} from '../../dex/wallet-avatar/wallet-avatar.component';
import {SelectInputModule} from '../../select-input';
import {SelectItem} from '../../select-input/select-item';
import {L10nModule} from '../../translate';
import {DynamicZyxDaemonService} from '../dynamic-zyx-daemon.service';
import {shortenWalletAddress} from '../utils/dyn-xyz-utils';
import {ShortenWalletAddrPipe} from '../utils/shorten-wallet-addr.pipe';
import {DynWalletDropdownButtonComponent} from './dyn-wallet-dropdown-button/dyn-wallet-dropdown-button.component';

@Component({
    selector:        'dyn-xyz-app-header',
    templateUrl:     './dyn-xyz-app-header.component.html',
    styleUrls:       ['./dyn-xyz-app-header.component.scss'],
    imports: [
        L10nModule,
        SelectInputModule,
        ReactiveFormsModule,
        DynWalletDropdownButtonComponent,
        ShortenWalletAddrPipe,
        WalletAvatarComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynXyzAppHeaderComponent implements OnInit {

    public readonly destroy$ = destroy$();

    public dynRemote = this.dynamicZyxDaemonService.dynRemote;

    public isLoggedIn: boolean;

    public walletFormControl: FormControl<string>;

    public walletSelectItems: SelectItem[];

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private dynamicZyxDaemonService: DynamicZyxDaemonService
    ) {}

    public ngOnInit(): void {
        this.dynamicZyxDaemonService.dynRemote$.pipe(
            switchMap(dynRemote => {
                if (dynRemote !== this.dynRemote) {
                    this.dynRemote = dynRemote;
                    this.changeDetectorRef.markForCheck();
                }

                return dynRemote
                    ? this.listenToDynAuth()
                    : NEVER;
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    public test(): void {
        this.dynRemote.showDynamicUserProfile();
    }

    public profileClick(): void {
        this.dynRemote.showDynamicUserProfile();
    }

    public logoutClick(): void {
        this.dynRemote.callLogout();
    }

    private listenToDynAuth(): Observable<any> {
        return this.dynRemote.isLoggedIn$.pipe(
            switchMap(isLoggedIn => {
                this.isLoggedIn = isLoggedIn;

                if (isLoggedIn) {
                    this.initWalletSelect();
                }

                this.changeDetectorRef.markForCheck();

                return NEVER;
            })
        );
    }

    private initWalletSelect(): void {
        this.walletFormControl = new FormControl('');

        const dynLogout$ = this.dynRemote.isLoggedIn$.pipe(
            filter(isLoggedIn => !isLoggedIn),
            tap(() => {
                this.walletFormControl = undefined;
                this.walletSelectItems = undefined;
                this.changeDetectorRef.markForCheck();
            })
        );

        merge(
            this.dynRemote.userWallets$.pipe(
                tap(() => this.populateWalletSelector())
            ),
            this.dynRemote.primaryWallet$.pipe(
                tap(() => this.resetWalletSelector())
            ),
            this.walletFormControl.valueChanges.pipe(
                tap(walletAddress => this.selectWallet(walletAddress))
            )
        ).pipe(
            tap(() => this.changeDetectorRef.markForCheck()),
            takeUntil(dynLogout$),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private resetWalletSelector(): void {
        const primaryWallet = this.dynRemote.primaryWallet$.value;

        const selectedWalletAddress: string = (primaryWallet as any)?.address
            || this.walletSelectItems?.[0]?.value
            || null;

        this.walletFormControl.reset(selectedWalletAddress);
    }

    private populateWalletSelector(): void {
        const wallets = this.dynRemote.userWallets$.value;

        this.walletSelectItems = wallets.map(wallet => ({
            value: (wallet as any).address,
            label: shortenWalletAddress((wallet as any).address)
        }));

        this.resetWalletSelector();
    }

    private selectWallet(walletAddress: string): void {
        const currentPrimaryWallet = this.dynRemote.primaryWallet$.value;

        if (currentPrimaryWallet?.address !== walletAddress && this.dynRemote?.switchWallet) {
            const wallet = this.dynRemote.userWallets$.value?.find(
                itWallet => itWallet.address === walletAddress
            );

            if (wallet) {
                this.dynRemote.switchWallet(wallet.id);
            }
        }
    }

}
